/**
 * One todo line: tappable checkbox circle + title, plus a trailing pencil
 * that opens the edit form. The toggle area and the pencil are SIBLING
 * touchables (never nested) so hits are unambiguous on web and native.
 * Completed rows fill the circle with the solid success token and pick the
 * check ink via onColor (solid fill — safe; see onColor docs).
 */
import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';
import { onColor } from '@/constants/shared/theme';
import type { ResidentTodo } from '@/types/residents/todos';

interface TodoRowProps {
  todo: ResidentTodo;
  isLast: boolean;
  onToggle: (id: string) => void;
  onEdit: (todo: ResidentTodo) => void;
  /** Optional right-aligned date (overdue rows show how old the item is). */
  dateLabel?: string;
}

export const TodoRow: React.FC<TodoRowProps> = React.memo(
  ({ todo, isLast, onToggle, onEdit, dateLabel }) => {
    const type = useTypography();
    const space = useSpacing();
    const radius = useRadius();
    const styles = useThemedStyles(({ colors, semantic }) =>
      StyleSheet.create({
        row: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: space.md,
          paddingVertical: space.lg,
        },
        rowBorder: {
          borderBottomWidth: 1,
          borderBottomColor: colors.separator,
        },
        toggleArea: {
          flex: 1,
          flexDirection: 'row',
          alignItems: 'center',
          gap: space.md,
        },
        checkbox: {
          width: 24,
          height: 24,
          borderRadius: radius.full,
          borderWidth: 2,
          borderColor: semantic.border.input,
          alignItems: 'center',
          justifyContent: 'center',
        },
        checkboxDone: {
          backgroundColor: semantic.status.success,
          borderColor: semantic.status.success,
        },
        checkmark: {
          ...type.captionMedium,
          color: onColor(semantic.status.success),
        },
        title: {
          ...type.body,
          flex: 1,
          color: colors.text.primary,
        },
        titleDone: {
          color: colors.text.muted,
          textDecorationLine: 'line-through',
        },
        dateLabel: {
          ...type.small,
          // secondary, not muted: this is informational text (how overdue an
          // item is), and muted is 4.48:1 on the light card — under the 4.5:1
          // AA floor for 13pt. secondary clears it in both themes.
          color: colors.text.secondary,
        },
        editIcon: {
          color: colors.text.muted,
        },
      }),
    );

    return (
      <View style={[styles.row, !isLast && styles.rowBorder]}>
        <TouchableOpacity
          style={styles.toggleArea}
          onPress={() => onToggle(todo.id)}
          activeOpacity={0.7}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: todo.completed }}
          accessibilityLabel={todo.title}
        >
          <View style={[styles.checkbox, todo.completed && styles.checkboxDone]}>
            {todo.completed ? <Text style={styles.checkmark}>✓</Text> : null}
          </View>
          <Text
            style={[styles.title, todo.completed && styles.titleDone]}
            numberOfLines={1}
          >
            {todo.title}
          </Text>
          {dateLabel ? (
            <Text style={styles.dateLabel} numberOfLines={1}>
              {dateLabel}
            </Text>
          ) : null}
        </TouchableOpacity>
        <TouchableOpacity
          onPress={() => onEdit(todo)}
          // 16pt icon + 14 slop per side = the 44pt minimum target.
          hitSlop={{ top: 14, bottom: 14, left: 14, right: 14 }}
          accessibilityRole="button"
          accessibilityLabel={`Edit ${todo.title}`}
        >
          <Icon name="pencil-outline" size={16} style={styles.editIcon} />
        </TouchableOpacity>
      </View>
    );
  },
);

TodoRow.displayName = 'TodoRow';
